import React, { ReactElement, FunctionComponent } from 'react';
import { useSnapshot } from 'valtio';
import { Panel, setSideBarOpenPanel, sideBarState } from '../sidebar-state';

import { Expansion } from './expansion';
import { ConfettiComp } from './confetti-comp';
import { Hints, HintsDefinition, ITSEventType } from 'its-pages';
import store from '../redux/store';
import Box from '@mui/material/Box';

export function getCorrectElement(key: string | number): JSX.Element {
  return (
    <div>
      <ConfettiComp key={key} />

      <h3>Congratulations! You are correct.</h3>
    </div>
  );
}

export function getSecondTryHint(
  isMultiChoice: boolean,
  hints?: Hints,
  isPositiveProblemQuality=true
): JSX.Element {
  return (
    <div>
      <p>
        <b>
          {isMultiChoice
            ? 'Not quite.  Review your responses and try again.'
            : 'Not quite.  That selection is incorrect.'}
        </b>
      </p>
      {
        getGeneralHint(hints, isPositiveProblemQuality)
      }
    </div>
  );
};

export function getGeneralHint(hints:Hints, isPositiveProblemQuality: boolean) {
  return hints && hints.length > 0 ? (
    <div>
      <p>{`Examples of ${isPositiveProblemQuality ? 'helpful' : 'poor'} responses include:`}</p>
      <ul style={{ listStyleType: 'disc' }}>
        {hints.map((hint: HintsDefinition) => (
          <li key={hint.reason.declarative}>
            {hint.reason.declarative}
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <div />
  );
}

export function getReviewMessage(
  streak: number,
  improvement: boolean
): JSX.Element {
  return (
    <div>
      {streak > -2 ? (
        improvement ? (
          <div>
            <p>You improved the second time around, good job.</p>
            <p>
              <b>
                Click on review to understand how you could have done even
                better.
              </b>
            </p>
          </div>
        ) : (
          <div>
            <p>Don’t worry, these are hard skills to learn.</p>
            <p>
              <b>Click on review to see where there is room for improvement.</b>
            </p>
          </div>
        )
      ) : (
        <div>
          <p>Don’t get discouraged, this is a great learning opportunity.</p>
          <p>
            <b>Click on review so it can help you.</b>
          </p>
        </div>
      )}{' '}
    </div>
  );
}

export function getDiscourseReviewMessage(
  streak: number,
  improvement: boolean
): JSX.Element {
  return (
    <div>
      {streak > -2 ? (
        improvement ? (
          <div>
            <p>You improved the second time around, good job.</p>
            <p>
              <b>
                Tap your selections to review the answers to understand
                how you may have done even better.
              </b>
            </p>
          </div>
        ) : (
          <div>
            <p>Don’t worry, these are hard skills to learn.</p>
            <p>
              <b>
                {' '}
                Tap your selections to review the answers to see where
                there is room for improvement.{' '}
              </b>
            </p>
          </div>
        )
      ) : (
        <div>
          <p> Don’t get discouraged, this is a great learning opportunity. </p>
          <p>
            <b>
              Tap your selections to review the answers so it can help
              you.
            </b>
          </p>
        </div>
      )}{' '}
    </div>
  );
}

export function getEvalReviewMessage(
  streak: number,
  improvement: boolean
): JSX.Element {
  return (
    <div>
      {streak > -2 ? (
        improvement ? (
          <div>
            <p>You improved the second time around, good job.</p>
            <p>
              <b>
                Next time you wll give an even better response the first
                attempt.
              </b>
            </p>
          </div>
        ) : (
          <div>
            <p>Don’t worry, these are hard skills to learn.</p>
            <p>
              <b>Next time you will get it on the first attempt.</b>
            </p>
          </div>
        )
      ) : (
        <div>
          <p>Don’t get discouraged, this is a great learning opportunity.</p>
          <p>
            <b>Your first attempt will be better next time.</b>
          </p>
        </div>
      )}{' '}
    </div>
  );
}

const Comp: FunctionComponent = (): ReactElement => {
  const { openPanel, expansionInfos } = useSnapshot(sideBarState);

  const onChange =
    (panel: Panel) => (_e: React.SyntheticEvent, expanded: boolean) => {
      if (expanded) {
        setSideBarOpenPanel(panel);
        if (panel === Panel.ResouceTips) {
          const state = store.getState();
          // log expansion of resourcesTips
          const pageContent = state.currentProblemState.pageContent;
          pageContent?.pageData.recordEvent(ITSEventType.openTips);
        }
      } else {
        setSideBarOpenPanel(Panel.None);
      }
    };

  const resourcesTipsInfo = expansionInfos[Panel.ResouceTips];
  const feedbackInfo = expansionInfos[Panel.Feedback];
  return (
    <Box sx={{ position: 'sticky', top: 0 }}>
      <Expansion
        expansionInfo={resourcesTipsInfo!}
        expanded={openPanel === 'resourcesTips'}
        onChange={onChange(Panel.ResouceTips)}
        summaryElement={<div>{'Resources & Tips'}</div>}
        animate={resourcesTipsInfo?.animate}
      />
      <Expansion
        expansionInfo={feedbackInfo!}
        expanded={openPanel === Panel.Feedback}
        onChange={onChange(Panel.Feedback)}
        summaryElement={<div>{'Feedback'}</div>}
        animate={feedbackInfo?.animate}
      />
    </Box>
  );
};

const SideBar = Comp;
export default SideBar;
